import React, { useState, useEffect, useCallback } from 'react';
import { Textarea } from '@chakra-ui/react';
import PropTypes from 'prop-types';

const FastTextarea = React.memo(
  ({ name, value, onChange, disabled, className, placeholder, onBlur, ...props }) => {
    const [customValue, setCustomValue] = useState(value || '');

    const handleChange = useCallback(e => setCustomValue(e.target.value), []);

    useEffect(() => {
      setCustomValue(value || '');
    }, [value]);

    useEffect(() => {
      const timeout = setTimeout(() => {
        if (onChange) {
          // Check if onChange expects an event object or just the value
          if (onChange.length > 1 || onChange.toString().includes('event')) {
            // If it expects an event object
            onChange({ target: { name, value: customValue } });
          } else {
            // If it expects just the value
            onChange(customValue);
          }
        }
      }, 300);
      return () => clearTimeout(timeout);
    }, [customValue, onChange, name]);

    const handleBlur = useCallback(
      e => {
        // Ensure the latest value is saved on blur
        if (onChange) {
          if (onChange.length > 1 || onChange.toString().includes('event')) {
            onChange({ target: { name, value: customValue } });
          } else {
            onChange(customValue);
          }
        }
        // Call the original onBlur if provided
        if (onBlur) {
          onBlur(e);
        }
      },
      [customValue, onChange, onBlur, name]
    );

    return (
      <Textarea
        value={customValue ?? ''}
        disabled={disabled}
        onChange={handleChange}
        onBlur={handleBlur}
        autoComplete="off"
        placeholder={placeholder}
        {...(className ? { className } : {})}
        {...(name ? { name } : {})}
        {...props}
      />
    );
  },
  (prev, next) =>
    prev.value === next.value &&
    prev.disabled === next.disabled &&
    prev.className === next.className &&
    prev.placeholder === next.placeholder
);

export default FastTextarea;

FastTextarea.propTypes = {
  name: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  placeholder: PropTypes.string,
};